import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import AddIcon from '@mui/icons-material/Add';
import { IconButton } from '@mui/material';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { PopoverState } from '../../hooks/useItemPopover';

export interface DisplayPermanentInjury {
  id: string;
  name: string;
  description: string;
  effectLabel?: string;
}

interface PermanentInjuriesProps {
  injuries: DisplayPermanentInjury[];
  editMode?: boolean;
  onAdd?: () => void;
  onRemove?: (injury: DisplayPermanentInjury, index: number) => void;
}

export const PermanentInjuries: React.FC<PermanentInjuriesProps> = ({ injuries, editMode = false, onAdd, onRemove }) => {
  const [pending, setPending] = React.useState<{ injury: DisplayPermanentInjury; index: number } | null>(null);
  const [details, setDetails] = React.useState<PopoverState | null>(null);
  const canRemove = editMode && Boolean(onRemove);

  if (!canRemove && injuries.length === 0) return null;

  return <Stack direction="column" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Permanent Injuries</Typography>
      {editMode && onAdd && <IconButton size="small" aria-label="Add permanent injury" onClick={onAdd}><AddIcon fontSize="small" /></IconButton>}
    </Stack>
    {injuries.length === 0 ? (
      <Typography variant="body2" color="text.secondary">None</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {injuries.map((injury, index) =>
          <Chip
            key={`${injury.id}-${index}`}
            size="small"
            icon={<PersonalInjuryIcon />}
            label={injury.effectLabel ? `${injury.name} (${injury.effectLabel})` : injury.name}
            color="error"
            variant="outlined"
            clickable
            onClick={(event) => setDetails({
              anchor: event.currentTarget,
              id: injury.id,
              name: injury.name,
              description: injury.description,
              tags: [],
              modifiers: injury.effectLabel,
            })}
            onDelete={canRemove ? () => setPending({ injury, index }) : undefined}
          />)}
      </Box>
    )}
    <ConfirmActionDialog
      open={pending !== null}
      title="Remove permanent injury"
      message={pending ? `Remove "${pending.injury.name}"?` : ''}
      confirmLabel="Remove"
      confirmColor="error"
      onClose={() => setPending(null)}
      onConfirm={() => {
        if (!pending || !onRemove) return;
        onRemove(pending.injury, pending.index);
        setPending(null);
      }}
    />
    <ItemDetailsPopover popover={details} onClose={() => setDetails(null)} />
  </Stack>;
};
